import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TableCommunicationService } from '../table-communication.service';
import { AiAssistantService, AssistantEventHandler } from 'src/app/services/ai-assistant/ai-assistant.service';
import { AssistantMessage } from 'src/app/services/ai-assistant/models';

@Injectable({
  providedIn: 'root'
})
export class AssistantService {
  // Observable to track if assistant is currently processing a request
  private isProcessingSubject = new BehaviorSubject<boolean>(false);
  public isProcessing$ = this.isProcessingSubject.asObservable();

  // Observable to track the current context being sent to the assistant
  private contextSubject = new BehaviorSubject<any>(null);
  public context$ = this.contextSubject.asObservable();

  // Observable to track any errors that occur during assistant interactions
  private errorSubject = new BehaviorSubject<string | null>(null);
  public error$ = this.errorSubject.asObservable();

  constructor(
    private tableCommunicationService: TableCommunicationService,
    private aiAssistantService: AiAssistantService
  ) {
    // Dynamic event handlers will be registered when buildAssistantContext is called
  }

  /**
   * Sends the current table context to the assistant
   * @param context The context data to send
   */
  public sendTableContext(context: any): void {
    try {
      this.contextSubject.next(context);
    } catch (error) {
      this.handleError('Failed to send table context', error);
    }
  }

  /**
   * Processes a natural language query against the current table data
   * @param query The natural language query to process
   */
  public async processQuery(query: string): Promise<void> {
    try {
      this.isProcessingSubject.next(true);
      // TODO: Implement query processing logic
      // This will involve:
      // 1. Parsing the natural language query
      // 2. Converting it to table operations
      // 3. Executing those operations via the table communication service
    } catch (error) {
      this.handleError('Failed to process query', error);
    } finally {
      this.isProcessingSubject.next(false);
    }
  }

  /**
   * Handles table-specific operations requested by the assistant
   * @param operation The operation to perform
   * @param params Parameters for the operation
   */
  public async handleTableOperation(operation: string, params: any): Promise<void> {
    try {
      this.isProcessingSubject.next(true);
      // TODO: Implement table operation handling
      // This will involve:
      // 1. Validating the operation
      // 2. Converting it to appropriate table commands
      // 3. Executing via table communication service
    } catch (error) {
      this.handleError('Failed to handle table operation', error);
    } finally {
      this.isProcessingSubject.next(false);
    }
  }

  /**
   * Resets the assistant's state
   */
  public reset(): void {
    this.isProcessingSubject.next(false);
    this.contextSubject.next(null);
    this.errorSubject.next(null);
  }

  /**
   * Handles errors that occur during assistant operations
   * @param message Error message
   * @param error The error object
   */
  private handleError(message: string, error: any): void {
    console.error(message, error);
    this.errorSubject.next(message);
    this.isProcessingSubject.next(false);
  }

  /**
   * Gets the current table state for the assistant
   * @returns The current table state
   */
  public getTableState(): any {
    // TODO: Implement table state gathering
    // This should return:
    // - Current column configuration
    // - Current filter state
    // - Current sort state
    // - Selected rows
    // - Current page information
    return {};
  }

  /**
   * Updates the table based on assistant recommendations
   * @param recommendations The recommendations to apply
   */
  public async applyRecommendations(recommendations: any): Promise<void> {
    try {
      this.isProcessingSubject.next(true);
      // TODO: Implement recommendation application
      // This will involve:
      // 1. Validating recommendations
      // 2. Converting to table operations
      // 3. Applying changes via table communication service
    } catch (error) {
      this.handleError('Failed to apply recommendations', error);
    } finally {
      this.isProcessingSubject.next(false);
    }
  }

  public buildAssistantContext(params: any): any {
    return null;
  }
}
